// @flow
import React, { createContext, useContext } from 'react';
import type { Node } from 'react';
import type { MapType, Context, FeatureListeners } from './types';

const OpenLayersContext = createContext<Context>({});

type Props = $ReadOnly<{
    map: ?MapType,
    featureListeners: FeatureListeners,
    children: Node
}>;

export const OpenLayersProvider = ({ map, featureListeners, children }: Props) => {
    const addFeatureListener = (feature, type, listener) => {
        let listeners = featureListeners[type];

        if(!listeners) {
            listeners = new Map();
            featureListeners[type] = listeners;
        }

        listeners.set(feature, listener);
    };

    const removeFeatureListener = (feature, type) => {
        const listeners = featureListeners[type];

        if(listeners && listeners.get(feature)) {
            listeners.delete(feature);
        }
    };

    const context = {
        map,
        featureListeners,
        addFeatureListener,
        removeFeatureListener
    };

    return (
        <OpenLayersContext.Provider value={context}>
            {children}
        </OpenLayersContext.Provider>
    );
};

export const useOpenLayersContext = () => useContext(OpenLayersContext);

export const useMap = () => {
    const context = useOpenLayersContext();
    return context.map;
};

export const useView = () => {
   const map = useMap();
   return map && map.getView();
};

export const useResolution = () => {
    const view = useView();
    return view && view.getResolution();
};

export const useFeatureListeners = () => {
    const context = useOpenLayersContext();
    return context.featureListeners;
};

export const useAddFeatureListener = () => {
    const context = useOpenLayersContext();
    return context.addFeatureListener;
};

export const useRemoveFeatureListener = () => {
    const context = useOpenLayersContext();
    return context.removeFeatureListener;
};